import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Navigation2,
  MapPin,
  Clock,
  Truck,
  CheckCircle2,
  AlertTriangle,
  FileSignature,
  ExternalLink,
  Users,
  ChevronLeft,
  Calendar,
  Share2,
  PhoneCall,
  Check,
  ShieldCheck,
  CircleDot
} from 'lucide-react';
import { useAreaMotorista } from '../../contexts/AreaMotoristaContext';
import { AreaMotoristaAssinaturaModal } from './AreaMotoristaAssinaturaModal';
import { AreaMotoristaOcorrenciaModal } from './AreaMotoristaOcorrenciaModal';
import { sanitizeText } from '../../utils/textSanitizer';
import { formatDateBR } from '../../utils/dateUtils';

export const AreaMotoristaExecucaoOS: React.FC = () => {
  const {
    viagemAtiva,
    ordensServico,
    setViagemAtiva,
    isAgregado,
    iniciarViagem,
    chegadaColeta,
    iniciarTransporte,
    chegadaEntrega,
    finalizarEntrega,
    registrarOcorrencia,
    isSaving
  } = useAreaMotorista();

  const navigate = useNavigate();

  const [modalAssinaturaOpen, setModalAssinaturaOpen] = useState(false);
  const [modalOcorrenciaOpen, setModalOcorrenciaOpen] = useState(false);
  const [etapaAtual, setEtapaAtual] = useState<number>(1); // 1 = A caminho coleta, 2 = Na coleta, 3 = Em trânsito entrega, 4 = No destino, 5 = Concluído

  // Se não tem viagem ativa, seleciona a primeira disponível ou mostra mensagem
  const os = viagemAtiva || ordensServico.find(o => o.status_id !== 4 && !o.assinatura) || ordensServico[0];

  const isFinalizada = os ? (os.status_id === 4 || !!os.assinatura) : false;

  useEffect(() => {
    if (os?.ocorrencias) {
      const oc = os.ocorrencias.toLowerCase();
      if (oc.includes('chegada no destino') || oc.includes('iniciando desembarque')) {
        setEtapaAtual(5);
      } else if (oc.includes('carga embarcada') || oc.includes('em trânsito para entrega')) {
        setEtapaAtual(4);
      } else if (oc.includes('chegada no local de coleta') || oc.includes('aguardando carregamento')) {
        setEtapaAtual(3);
      } else if (oc.includes('início de deslocamento') || oc.includes('viagem iniciada')) {
        setEtapaAtual(2);
      } else {
        setEtapaAtual(1);
      }
    } else {
      setEtapaAtual(1);
    }
  }, [os?.ocorrencias, os?.id]);

  const handleOpenGoogleMaps = (endereco?: string) => {
    if (!endereco) return;
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(endereco)}`, '_blank');
  };

  const handleOpenWaze = (endereco?: string) => {
    if (!endereco) return;
    window.open(`https://waze.com/ul?q=${encodeURIComponent(endereco)}&navigate=yes`, '_blank');
  };

  if (!os) {
    return (
      <div className="p-12 text-center rounded-3xl border border-slate-800 bg-slate-950/60 space-y-4">
        <Truck className="w-12 h-12 mx-auto text-slate-600 stroke-[1.5]" />
        <h2 className="text-base font-bold text-white">Nenhuma viagem selecionada para execução</h2>
        <p className="text-xs text-slate-400">Escolha uma viagem na sua lista de viagens para iniciar o trajeto.</p>
        <button
          onClick={() => navigate('/area-motorista/viagens')}
          className="px-5 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-xs inline-flex items-center gap-2 shadow-lg shadow-red-600/30 transition"
        >
          <Navigation2 className="w-4 h-4" />
          <span>Ver Minhas Viagens</span>
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-5 text-left">
      
      {/* Top Bar with Navigation & Title */}
      <div className="flex items-center justify-between gap-3">
        <button
          onClick={() => navigate('/area-motorista/viagens')}
          className="flex items-center gap-1 text-xs text-slate-400 hover:text-white font-bold transition"
        >
          <ChevronLeft className="w-4 h-4" />
          <span>Voltar para Lista</span>
        </button>

        <button
          onClick={() => setModalOcorrenciaOpen(true)}
          className="px-3 py-1.5 rounded-xl bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/30 text-xs font-bold flex items-center gap-1.5 transition"
        >
          <AlertTriangle className="w-3.5 h-3.5" />
          <span>Ocorrência / Alerta</span>
        </button>
      </div>

      {/* Main OS Header Card */}
      <div className="rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-5 sm:p-6 shadow-2xl space-y-4">
        
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-800 pb-3">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-black text-white">Execução da Viagem • OS #{os.id}</h1>
              <span
                className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                  isFinalizada
                    ? 'bg-emerald-950 text-emerald-400 border border-emerald-800'
                    : 'bg-red-950 text-red-400 border border-red-800 animate-pulse'
                }`}
              >
                {isFinalizada ? 'Entrega Finalizada' : 'Em Rota Ativa'}
              </span>
            </div>
            <p className="text-xs text-slate-400 mt-0.5">
              Carga: <strong className="text-slate-200">{sanitizeText(os.descricao) || 'Carga Padrão'}</strong>
            </p>
          </div>

          <div className="text-right">
            {isAgregado ? (
              <>
                <span className="text-[10px] text-slate-500 uppercase tracking-wider block">Repasse do Motorista</span>
                <span className="text-base font-black text-emerald-400 font-mono">
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(os.repasse) || 0)}
                </span>
              </>
            ) : (
              <>
                <span className="text-[10px] text-slate-500 uppercase tracking-wider block">Regime Operacional</span>
                <span className="text-xs font-bold text-blue-400">
                  Motorista Frota Própria / CLT
                </span>
              </>
            )}
          </div>
        </div>

        {/* Origin / Coleta Details */}
        <div className="p-4 rounded-2xl bg-slate-900/90 border border-slate-800 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-blue-400 flex items-center gap-1.5 uppercase tracking-wider">
              <MapPin className="w-4 h-4" />
              1. Ponto de Coleta / Retirada
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleOpenGoogleMaps(os.retirada)}
                className="px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-[11px] font-bold text-slate-200 flex items-center gap-1 border border-slate-700 transition"
              >
                <span>Google Maps</span>
                <ExternalLink className="w-3 h-3 text-slate-400" />
              </button>
              <button
                onClick={() => handleOpenWaze(os.retirada)}
                className="px-2.5 py-1 rounded-lg bg-sky-600/20 hover:bg-sky-600/30 text-sky-400 text-[11px] font-bold flex items-center gap-1 border border-sky-500/30 transition"
              >
                <span>Waze</span>
              </button>
            </div>
          </div>

          <p className="text-sm font-semibold text-slate-100 pl-5 leading-relaxed">
            {sanitizeText(os.retirada) || 'Endereço de Coleta não especificado'}
          </p>
        </div>

        {/* Destination / Entrega Details */}
        <div className="p-4 rounded-2xl bg-slate-900/90 border border-slate-800 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-emerald-400 flex items-center gap-1.5 uppercase tracking-wider">
              <MapPin className="w-4 h-4" />
              2. Ponto de Destino / Entrega
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleOpenGoogleMaps(os.entrega)}
                className="px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-[11px] font-bold text-slate-200 flex items-center gap-1 border border-slate-700 transition"
              >
                <span>Google Maps</span>
                <ExternalLink className="w-3 h-3 text-slate-400" />
              </button>
              <button
                onClick={() => handleOpenWaze(os.entrega)}
                className="px-2.5 py-1 rounded-lg bg-sky-600/20 hover:bg-sky-600/30 text-sky-400 text-[11px] font-bold flex items-center gap-1 border border-sky-500/30 transition"
              >
                <span>Waze</span>
              </button>
            </div>
          </div>

          <p className="text-sm font-semibold text-slate-100 pl-5 leading-relaxed">
            {sanitizeText(os.entrega) || 'Endereço de Entrega não especificado'}
          </p>
        </div>

        {/* Operational info bar */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs text-slate-400 pt-1">
          {Number(os.quantidade_ajudante) > 0 && (
            <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800 flex items-center gap-2">
              <Users className="w-4 h-4 text-slate-400" />
              <span><strong>{os.quantidade_ajudante}</strong> Ajudante(s)</span>
            </div>
          )}
          {os.horario_inicio && (
            <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800 flex items-center gap-2">
              <Clock className="w-4 h-4 text-slate-400" />
              <span>Início: <strong>{os.horario_inicio}</strong></span>
            </div>
          )}
          {os.data_atendimento && (
            <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              <span>{formatDateBR(os.data_atendimento)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Execution Progression Steps & Status Actions */}
      {isFinalizada ? (
        <div className="rounded-3xl border border-emerald-800 bg-emerald-950/40 p-6 text-center space-y-4 shadow-xl">
          <div className="w-16 h-16 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-10 h-10" />
          </div>
          <div className="space-y-1">
            <h2 className="text-lg font-black text-white">Viagem Concluída com Sucesso!</h2>
            <p className="text-xs text-emerald-200">
              Assinatura digital e dados do recebedor arquivados com sucesso na OS #{os.id}.
            </p>
          </div>

          {os.rg_assinatura && (
            <div className="p-3 rounded-xl bg-slate-900/80 border border-slate-800 text-xs text-slate-300 max-w-sm mx-auto">
              <p>Documento Registrado: <strong>{os.rg_assinatura}</strong></p>
              {os.horario_final && <p>Horário de Finalização: <strong>{os.horario_final}</strong></p>}
            </div>
          )}

          {os.assinatura && (
            <div className="space-y-1 max-w-xs mx-auto">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Assinatura Digital Coletada</span>
              <div className="p-2 rounded-xl bg-white border border-slate-300">
                <img src={os.assinatura} alt="Assinatura" className="max-h-24 mx-auto object-contain" />
              </div>
            </div>
          )}

          <div className="pt-2">
            <button
              onClick={() => navigate('/area-motorista/viagens')}
              className="px-6 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold transition shadow"
            >
              Ver Outras Viagens
            </button>
          </div>
        </div>
      ) : (
        <div className="rounded-3xl border border-slate-800 bg-slate-950/70 p-5 space-y-5 shadow-xl">
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <h2 className="text-sm font-bold text-white flex items-center gap-2">
              <CircleDot className="w-4 h-4 text-red-500" />
              <span>Etapas de Execução da Rota</span>
            </h2>
            <span className="text-xs text-slate-400">Atualize conforme avança</span>
          </div>

          {/* Action Step Buttons */}
          <div className="space-y-3">
            
            {/* Etapa 1: A Caminho da Coleta */}
            <div className="p-3.5 rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-between gap-3">
              <div className="space-y-0.5">
                <span className="text-xs font-bold text-white block">1. A Caminho da Coleta</span>
                <span className="text-[11px] text-slate-400">Deslocamento iniciado até a origem</span>
              </div>
              <button
                onClick={() => {
                  iniciarViagem(os.id);
                  setEtapaAtual(2);
                }}
                disabled={isSaving}
                className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold shrink-0 shadow transition disabled:opacity-50"
              >
                {etapaAtual >= 2 ? '✓ Em Rota' : 'Iniciar'}
              </button>
            </div>

            {/* Etapa 2: Cheguei na Coleta */}
            <div className="p-3.5 rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-between gap-3">
              <div className="space-y-0.5">
                <span className="text-xs font-bold text-white block">2. Chegada na Coleta</span>
                <span className="text-[11px] text-slate-400">No galpão/doca aguardando carregamento</span>
              </div>
              <button
                onClick={() => {
                  chegadaColeta(os.id);
                  setEtapaAtual(3);
                }}
                disabled={isSaving}
                className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 text-xs font-bold shrink-0 transition disabled:opacity-50"
              >
                {etapaAtual >= 3 ? '✓ Cheguei' : 'Cheguei'}
              </button>
            </div>

            {/* Etapa 3: Em Trânsito para Entrega */}
            <div className="p-3.5 rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-between gap-3">
              <div className="space-y-0.5">
                <span className="text-xs font-bold text-white block">3. Carga Embarcada / Em Trânsito</span>
                <span className="text-[11px] text-slate-400">A caminho do destinatário final</span>
              </div>
              <button
                onClick={() => {
                  iniciarTransporte(os.id);
                  setEtapaAtual(4);
                }}
                disabled={isSaving}
                className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 text-xs font-bold shrink-0 transition disabled:opacity-50"
              >
                {etapaAtual >= 4 ? '✓ Em Trânsito' : 'Em Trânsito'}
              </button>
            </div>

            {/* Etapa 4: Cheguei no Destino */}
            <div className="p-3.5 rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-between gap-3">
              <div className="space-y-0.5">
                <span className="text-xs font-bold text-white block">4. Chegada no Destino</span>
                <span className="text-[11px] text-slate-400">Descarregando no endereço do cliente</span>
              </div>
              <button
                onClick={() => {
                  chegadaEntrega(os.id);
                  setEtapaAtual(5);
                }}
                disabled={isSaving}
                className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 text-xs font-bold shrink-0 transition disabled:opacity-50"
              >
                {etapaAtual >= 5 ? '✓ No Destino' : 'No Destino'}
              </button>
            </div>
          </div>

          {/* Final CTA Button: Digital Signature & Delivery Completion */}
          <div className="pt-2">
            <button
              onClick={() => setModalAssinaturaOpen(true)}
              className="w-full py-4 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-black text-sm sm:text-base flex items-center justify-center gap-2 shadow-xl shadow-emerald-600/30 transition transform active:scale-[0.99]"
            >
              <FileSignature className="w-5 h-5" />
              <span>Finalizar Viagem & Coletar Assinatura</span>
            </button>
          </div>
        </div>
      )}

      {/* Modais */}
      <AreaMotoristaAssinaturaModal
        isOpen={modalAssinaturaOpen}
        onClose={() => setModalAssinaturaOpen(false)}
        os={os}
        onSalvar={finalizarEntrega}
        isSaving={isSaving}
      />

      <AreaMotoristaOcorrenciaModal
        isOpen={modalOcorrenciaOpen}
        onClose={() => setModalOcorrenciaOpen(false)}
        os={os}
        onSalvarOcorrencia={registrarOcorrencia}
        isSaving={isSaving}
      />

    </div>
  );
};
